import React, { useEffect, useState } from 'react';
import TextField from '@mui/material/TextField';
import AutocompleteMui from '@mui/material/Autocomplete';
import { InputLabel } from '@wajeez-web/components/ui';
import { useTranslation } from 'react-i18next';
import { ReturnTranslatedText } from '@wajeez-web/core/localization';
import { ControllerFieldState } from 'react-hook-form';
import { AiOutlineClose } from 'react-icons/ai';

type AutocompleteGroupedOption = {
  value: string | undefined;
  label: string | undefined;
  group?: string;
};

interface AutocompleteGroupedProps {
  options: AutocompleteGroupedOption[];
  onChange: (value: string | undefined) => void;
  id?: string | undefined;
  value?: string | undefined;
  label?: string;
  color?: string;
  required?: boolean;
  isError?: boolean;
  fieldState?: ControllerFieldState;
  sxStyle?: { [key: string]: string | number };
}

export const AutocompleteGrouped = React.forwardRef<
  typeof AutocompleteMui,
  AutocompleteGroupedProps
>(
  (
    {
      id,
      options,
      onChange,
      label,
      value,
      required,
      isError,
      fieldState,
      color = '#1890ff',
      sxStyle = { width: 300 },
    },
    ref
  ) => {
    const [selectedOptions, setSelectedOptions] = useState<
      (AutocompleteGroupedOption | null)[]
    >([]);
    const [shownOptions, setShownOptions] =
      useState<AutocompleteGroupedOption[]>(options);

    const { t } = useTranslation();

    useEffect(() => {
      const valueParsed: string[] = JSON.parse(value || '[]');

      if (valueParsed) {
        setSelectedOptions(
          options.filter((o) => {
            return o.value && valueParsed.includes(o.value);
          })
        );
      }
    }, [value, options.length]);

    useEffect(() => {
      onChange(
        JSON.stringify(selectedOptions.map((s) => s?.value || '') || [])
      );

      let shownOptions: AutocompleteGroupedOption[] = options;

      for (const selectedOption of selectedOptions) {
        shownOptions = shownOptions.filter(
          (o) => o.group !== selectedOption?.group
        );
      }

      setShownOptions(shownOptions);
    }, [selectedOptions.length, options.length]);

    const renderSelectedOptions = () => {
      return selectedOptions.map((s, i) => {
        return (
          <div
            key={s?.value}
            className={`ml-1 mt-1 p-1 rounded bg-[#ebebeb] text-[14px]`}
          >
            <div className="font-bold">{s?.group}</div>
            <div className="mt-1 flex justify-center items-center">
              {s?.label}
              <div
                className="mr-4 text-[10px] cursor-pointer"
                onClick={() => {
                  setSelectedOptions(
                    selectedOptions.filter((ss, ii) => ii !== i)
                  );
                }}
              >
                <AiOutlineClose />
              </div>
            </div>
          </div>
        );
      });
    };

    return (
      <div className="relative">
        {label && (
          <InputLabel
            name={id ?? ''}
            text={t(`inputs.labels.${label}`, label.toString() || '') as string}
            required={required}
          />
        )}
        <div className="h-[30px]">
          <AutocompleteMui
            ref={ref}
            blurOnSelect
            disablePortal
            id={id}
            options={shownOptions}
            groupBy={(option) => {
              return option.group || '';
            }}
            value={null}
            onChange={(e, selection) => {
              setSelectedOptions([...selectedOptions, selection]);
            }}
            sx={{
              '&.MuiAutocomplete-root': {
                height: '100%',
                width: '100%',
              },
              '&.MuiAutocomplete-root .MuiFormControl-root': {
                height: '100%',
                width: '100%',
              },
              '&.MuiAutocomplete-root .MuiFormControl-root .MuiInputBase-root':
                {
                  height: '100%',
                  paddingTop: 0,
                  paddingBottom: 0,
                },
              //   '&.MuiAutocomplete-root .MuiInputBase-input': {
              //     opacity: '0',
              //   },
              '&.MuiAutocomplete-root .MuiOutlinedInput-input:focus': {
                border: 'none',
                boxShadow: 'none',
              },
              '&.MuiAutocomplete-root .MuiInputLabel-root': {
                transition: 'all 0.2s ease-out',
              },
              '&.MuiAutocomplete-root .MuiInputLabel-root:not(.MuiInputLabel-shrink)':
                {
                  top: '50%',
                  transform: 'translateY(-50%)',
                  left: '10px',
                  fontSize: '14px',
                },
              '&.MuiAutocomplete-root .MuiInputLabel-root.Mui-focused': {
                color,
              },
              '&.MuiAutocomplete-root .MuiInputLabel-shrink': {
                display: 'none',
              },
              '&.MuiAutocomplete-root input': {
                boxSizing: 'border-box',
                height: '100%',
              },
              '&.MuiAutocomplete-root fieldset': {
                top: 0,
                height: '100%',
              },
              '&.MuiAutocomplete-root legend': {
                display: 'none',
              },
              '&.MuiAutocomplete-root .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
                {
                  borderColor: color,
                },
              ...sxStyle,
            }}
            renderInput={(params) => <TextField {...params} label={label} />}
          />
        </div>
        {isError && (
          <div className="absolute top-[105%] right-0 text-[12px] text-red-500">
            <ReturnTranslatedText
              origintext={fieldState?.error?.message}
              text={`general.${fieldState?.error?.message}`}
            />
          </div>
        )}
        {selectedOptions.length ? (
          <div className="mt-2 w-full px-2 border border-[#666565] rounded-[6px]">
            <div
              className="mt-2 text-[#373737] text-[12px] font-medium cursor-pointer"
              onClick={() => {
                setSelectedOptions([]);
              }}
            >
              احذف الكل
            </div>
            <div className="w-full pb-2 pt-1 flex justify-start items-center flex-wrap">
              {renderSelectedOptions()}
            </div>
          </div>
        ) : null}
      </div>
    );
  }
);
